import React from 'react';
import Query from 'devextreme/data/query';
import DataGrid from 'devextreme/ui/data_grid';
import CustomStore from 'devextreme/data/custom_store';
import { CustomOperation } from 'devextreme-react/data-grid';
import {
  format,
  parse,
  startOfYesterday,
  addHours,
  endOfYesterday,
  startOfToday,
  endOfToday,
  startOfTomorrow,
  endOfTomorrow,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth
} from 'date-fns';
import { TextField } from '@material-ui/core';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { postWithToken } from 'api/network';
import dxTreeView from "devextreme/ui/tree_view";
import dxPopup from 'devextreme/ui/popup';

DataGrid.defaultOptions({
  device: [
    { deviceType: 'desktop' },
    { deviceType: 'tablet' },
    { deviceType: 'phone' }
  ], 
  options: {  
    selection: {  
      maxFilterLengthInRequest: 0  // Allows unlimited length of filter on grid data requests. Else, if exceeding default value of 1500, the request will remove the filter and query the whole dataset which breaks grid functionality due to size.
    }  
  }
})

dxTreeView.defaultOptions({ // Adds search to fields dropdown on all devex filter builders
  device: [
    { deviceType: 'desktop' },
    { deviceType: 'tablet' },
    { deviceType: 'phone' }
  ], 
  options: { 
    onInitialized: (e) => { 
      const { component } = e; 
      if (component.option('cssClass')?.includes('dx-filterbuilder-fields')) { 
        component.option('searchEnabled', true); 
      } 
    }
  } 
}); 

dxPopup.defaultOptions({
  device: [
    { deviceType: 'desktop' },
    { deviceType: 'tablet' },
    { deviceType: 'phone' }
  ],
  options: { 
    onContentReady: (e) => { // For fields dropdown with search, adjusts height of content so that the last two items are not cut off by the added height of the search bar
      const popupContent = e.component.content();
      const scrollable = popupContent.querySelector('.dx-treeview-with-search .dx-scrollable');
      if (!scrollable) return;
      const scrollableContent = scrollable.querySelector('.dx-scrollable-content');
      if (!scrollableContent) return;
      if (scrollableContent.offsetHeight > scrollable.offsetHeight) {
        const maxHeight = Math.max(scrollable.getBoundingClientRect().height - 36, 0)
        scrollable.style.maxHeight = `${maxHeight}px`;
      }
    } 
  } 
}); 

const defaultSortColumnChooserByName = () => {
  DataGrid.registerModule("columnChooserSorting", {
    extenders: {
      controllers: {
        columns: {
          getChooserColumns: function (loadAllColumns) {
            var result = this.callBase(loadAllColumns);
            result.sort((column1, column2) => column1.caption.localeCompare(column2.caption));
            return result;
          }
        }
      }
    }
  });
}

const filterOperations = {
  number: [
    '=',
    '<>',
    '<',
    '>',
    '<=',
    '>=',
    'isblank',
    'isnotblank',
    'between',
    'anyof',
    'noneof',
    'isAnyOfPaste',
    'isNoneOfPaste'
  ],
  string: [
    '=',
    '<>',
    'contains',
    'notcontains',
    'startswith',
    'endswith',
    'isblank',
    'isnotblank',
    'anyof',
    'noneof',
    'isAnyOfPaste',
    'isNoneOfPaste'
  ],
  stringDynamic: [
    '=',
    '<>',
    'isblank',
    'isnotblank',
    'anyof',
    'noneof',
  ],
  boolean: [
    '=',
    '<>',
    'isblank',
    'isnotblank',
    'anyof',
    'noneof'
  ],
  booleanDynamic: [
    '=',
    '<>',
    'isblank',
    'isnotblank'
  ]
}

const sortFields = (schema) => {
  let allowedFilterFields = schema.filter(c => c.allowFiltering === undefined || c.allowFiltering === true);  // For disabling filtering on columns
  let fields = [...allowedFilterFields].map(c => ({ dataField: c.field, caption: c.displayName, dataType: c.type, format: c.format }));
  let getCaption = f => f.caption.length > 0 ? f.caption.toLowerCase() : f.dataField.toLowerCase();
  fields.sort((a, b) => {
    if (getCaption(a) < getCaption(b))
      return -1;
    if (getCaption(a) > getCaption(b))
      return 1;
    return 0;
  });
  return fields;
}

const headerFilterSort = (options, dataSource, field, type, headerFilter) => {
  if (!dataSource || !field) return null;
  let notSortByAsc = !headerFilter || headerFilter.sortOrder !== "asc";
  let sortByDesc = headerFilter?.sortOrder === "desc";
  options.dataSource.postProcess = arr => { // Remove any values of undefined and convert to null. Otherwise filter will not work because of columns' calculateSortValue which sets undefined sort value to null.
    if (!arr?.length) return arr;
    const firstElIsBlank = arr[0].value == null;
    const lastElIsBlank = arr[arr.length - 1] == null || arr[arr.length - 1].value == null;
    if (lastElIsBlank) arr.pop(); // Remove any null or undefined values that may be at beginning or end of array, depending on sort order
    if (firstElIsBlank) arr.shift();
    if (firstElIsBlank || lastElIsBlank) {
      arr.unshift({ // Insert a (Blanks) option at the start. postProcess is called after sorting has been done so the Blanks option will be first regardless.
        key: null,
        value: null,
        text: "(Blanks)"
      });
    }
    return arr;
  }
  if (sortByDesc || (type === "number" && notSortByAsc)) {
    options.dataSource.group = { selector: field, desc: true };

    return null;
  };
  // By default, sort date and datetime desc
  if (type === 'datetime') {
    options.dataSource.postProcess = dateArr => {
      if (!dateArr || dateArr.length === 0) return dateArr;
      const timeData = [];

      // flattern the hierarchical yyyy/mm/dd/hh data time
      dateArr.forEach(year => {
        if (year.items) {
          year.items.forEach(month => {
            month.items.forEach(day => {
              day.items.forEach(hours => {
                hours.items.forEach(minutes => {
                  let parsedTime = parse(minutes.value, 'yyyy/MM/dd/H/mm', new Date());
                  let formattedTime = format(parsedTime, 'MM/dd/yyyy h:mm a');
                  minutes.text = formattedTime;
                  timeData.unshift(minutes); //unshift to reverse the sort order
                })
              });
            })
          });
        }
      });

      if (dateArr[0].text === '(Blanks)' || dateArr[dateArr.length - 1].text === '(Blanks)') { // If Blanks at start or end (null or undefined respectively)...
        // ...add (Blanks) choice as first value
        timeData.unshift(dateArr[0]);
      }
      return timeData;
    }
  }
  if (type === 'date') {
    options.dataSource.postProcess = dateArr => {
      if (!dateArr || dateArr.length === 0) return dateArr;
      const timeData = [];

      // flattern the hierarchical yyyy/mm/dd/hh data time
      dateArr.forEach(year => {
        if (year.items) {
          year.items.forEach(month => {
            month.items.forEach(day => {
              let timeParts = day.value.split('/');
              let yearPart = timeParts[0];
              let monthPart = timeParts[1];
              let dayPart = timeParts[2];
              day.text = `${monthPart}/${dayPart}/${yearPart}`;
              timeData.unshift(day); //unshift to reverse the sort order
            });
          });
        }
      });

      if (dateArr[0].text === '(Blanks)' || dateArr[dateArr.length - 1].text === '(Blanks)') { // If Blanks at start or end (null or undefined respectively)...
        // ...add (Blanks) choice as first value
        timeData.unshift(dateArr[0]);
      }
      return timeData;
    }
  }
  return null;
}

const registerModule = () => {
  defaultSortColumnChooserByName();
}

function prepareSortingForRequest(params, field, fieldType, headerFilter) { // Handle sorting for values in header filter panel
  const isDefaultDesc = ['number', 'date', 'datetime'].includes(fieldType);
  const sortByDesc = (isDefaultDesc && headerFilter?.sortOrder !== 'asc') || headerFilter?.sortOrder === 'desc';

  if (sortByDesc && params.group[0].desc == null) { // Filter out cases where user actually grouped by a field (desc will be false)
    params.group = [{ selector: field, isExpanded: false, desc: true }];
  }

  return params;
}

function prepareDates(dateArr, type) {
  if (type === 'datetime') {
    if (!dateArr || dateArr.length === 0) return dateArr;
    const timeData = [];
    let isBlank = false;

    // flattern the hierarchical yyyy/mm/dd/hh data time
    dateArr.forEach(year => {
      if (year.items && year.key) {
        year.items.forEach(month => {
          month.items.forEach(day => {
            day.items.forEach(hours => {
              hours.items.forEach(minutes => {
                minutes.value = minutes.value || `${year.key}/${month.key}/${day.key}/${hours.key}/${minutes.key}`
                let parsedTime = parse(minutes.value, 'yyyy/MM/dd/H/mm', new Date());
                const formattedTime = format(parsedTime, 'MM/dd/yyyy h:mm a');
                minutes.text = minutes.value;
                delete minutes.items;
                minutes.key = formattedTime;
                timeData.unshift(minutes); //unshift to reverse the sort order
              })
            });
          })
        });
      }

      if (year.key === null || year.key === '') {
        isBlank = true;
      }
    });

    if (isBlank) {
      timeData.unshift({ key: null, text: 'Blanks', value: null });
    }

    return timeData;
  }

  if (type === 'date') {
    if (!dateArr || dateArr.length === 0) return dateArr;
    const timeData = [];

    // flattern the hierarchical yyyy/mm/dd/hh data time
    let isBlank = false;
    dateArr.forEach(year => {
      if (year.items && year.key) {
        year.items.forEach(month => {
          month.items.forEach(day => {
            day.value = day.value || `${year.key}/${month.key}/${day.key}`
            let timeParts = day.value.split('/');
            let yearPart = timeParts[0];
            let monthPart = timeParts[1];
            let dayPart = timeParts[2];
            day.text = `${monthPart}/${dayPart}/${yearPart}`;
            delete day.items;
            day.key = day.text;
            timeData.unshift(day); //unshift to reverse the sort order
          });
        });
      }

      if (year.key === null || year.key === '') {
        isBlank = true;
      }
    });

    if (isBlank) {
      timeData.unshift({ key: null, text: 'Blanks', value: null });
    }

    return timeData;
  }
}

function sortBlanksValue(arr, groupParams){
  if (!arr?.length) return arr;

  const firstElIsBlank = arr[0].key === null || arr[0].key === '';
  const lastElIsBlank = arr[arr.length - 1].key === null || arr[arr.length - 1].key === '';
  let blanksItem;

  if (lastElIsBlank) blanksItem = arr.pop();
  if (firstElIsBlank && arr.length) blanksItem = arr.shift();
  if (firstElIsBlank || lastElIsBlank) {
    const blanksObject = {
      // for grouping action need to have original properties in payload
      // https://js.devexpress.com/Documentation/20_2/ApiReference/UI_Components/Errors_and_Warnings/#E1037
      ...blanksItem,
      value: null,
      text: "(Blanks)"
    }

    groupParams[0]?.desc !== true ? arr.unshift(blanksObject) : arr.push(blanksObject);
  }
  return arr;
}

const getFilterBuilderCustomOperations = () => {
  const toLocalString = (date) => {
    return format(date, `yyyy-MM-dd'T'HH:mm:ss.SSS`);
  }
  const operations = [{
    name: 'isAnyOfPaste',
    caption: 'Is any of (paste)',
    dataTypes: ['number', 'string'],
    editorRender: props => <TextField
      autoFocus
      defaultValue={props.value}
      inputProps={{ style: { fontSize: 13, padding: '0 0 5px 0' } }}
      onChange={e => {
        const parsed = e.target.value.split(/[\s,]+/).filter(el => {
          if (props.field.dataType === 'string') return true;
          return !isNaN(Number(el));
        }).map(el => {
          if (props.field.dataType === 'string') return el;
          return parseFloat(el);
        })
        props.setValue(parsed);
      }}
    />,
    calculateFilterExpression: (filterValue, field) => {
      const filter = [];
      filterValue.forEach(value => {
        filter.push([field.dataField, '=', value]);
        filter.push('or');
      });
      filter.pop();
      return filter;
    }
  }, {
    name: 'isNoneOfPaste',
    caption: 'Is none of (paste)',
    dataTypes: ['number', 'string'],
    editorRender: props => <TextField
      autoFocus
      defaultValue={props.value}
      inputProps={{ style: { fontSize: 13, padding: '0 0 5px 0' } }}
      onChange={e => {
        const parsed = e.target.value.split(/[\s,]+/).filter(el => {
          if (props.field.dataType === 'string') return true;
          return !isNaN(Number(el));
        }).map(el => {
          if (props.field.dataType === 'string') return el;
          return parseFloat(el);
        })
        props.setValue(parsed);
      }}
    />,
    calculateFilterExpression: (filterValue, field) => {
      const filter = [];
      filterValue.forEach(value => {
        filter.push([field.dataField, '=', value]);
        filter.push('or');
      });
      filter.pop();
      return ['!', filter];
    }
  }, {
    name: 'since5pmYesterday',
    caption: 'Since 5 PM yesterday',
    dataTypes: ['datetime'],
    hasValue: false,
    calculateFilterExpression: (filterValue, field) => {
      const fivePmYesterday = addHours(startOfYesterday(), 17);
      return [field.dataField, '>=', toLocalString(fivePmYesterday)]
    },
    icon: 'greaterorequal'
  }, {
    name: 'isYesterday',
    caption: 'Is yesterday',
    dataTypes: ['datetime'],
    hasValue: false,
    calculateFilterExpression: (filterValue, field) => {
      return [[field.dataField, '>=', toLocalString(startOfYesterday())], 'and', [field.dataField, '<=', toLocalString(endOfYesterday())]]
    },
    icon: 'range'
  }, {
    name: 'isToday',
    caption: 'Is today',
    dataTypes: ['datetime'],
    hasValue: false,
    calculateFilterExpression: (filterValue, field) => {
      return [[field.dataField, '>=', toLocalString(startOfToday())], 'and', [field.dataField, '<=', toLocalString(endOfToday())]]
    },
    icon: 'range'
  }, {
    name: 'isTomorrow',
    caption: 'Is tomorrow',
    dataTypes: ['datetime'],
    hasValue: false,
    calculateFilterExpression: (filterValue, field) => {
      return [[field.dataField, '>=', toLocalString(startOfTomorrow())], 'and', [field.dataField, '<=', toLocalString(endOfTomorrow())]]
    },
    icon: 'range'
  }, {
    name: 'isThisWeek',
    caption: 'Is this week',
    dataTypes: ['datetime'],
    hasValue: false,
    calculateFilterExpression: (filterValue, field) => {
      const now = new Date();
      return [[field.dataField, '>=', toLocalString(startOfWeek(now))], 'and', [field.dataField, '<=', toLocalString(endOfWeek(now))]]
    },
    icon: 'range'
  }, {
    name: 'isThisMonth',
    caption: 'Is this month',
    dataTypes: ['datetime'],
    hasValue: false,
    calculateFilterExpression: (filterValue, field) => {
      const now = new Date();
      return [[field.dataField, '>=', toLocalString(startOfMonth(now))], 'and', [field.dataField, '<=', toLocalString(endOfMonth(now))]]
    },
    icon: 'range'
  }];

  return operations.map(operation => {
    return <CustomOperation
      key={operation.name}
      name={operation.name}
      caption={operation.caption}
      dataTypes={operation.dataTypes}
      hasValue={operation.hasValue}
      editorRender={operation.editorRender}
      calculateFilterExpression={operation.calculateFilterExpression}
      icon={operation.icon}
    />
  })
};

const filterBuilderCustomOperations = getFilterBuilderCustomOperations();

const customStoreRequestParams = [
  'skip',
  'take',
  'requireTotalCount',
  'requireGroupCount',
  'sort',
  'filter',
  'totalSummary',
  'group',
  'groupSummary'
];

function isNotEmpty(value) {
  return value !== undefined && value !== null && value !== '';
}

function getLookupFilter(loadOptions) {
  const { searchExpr, searchOperation, searchValue } = loadOptions;
  const filter = [[searchExpr, searchOperation, searchValue]];

  return searchValue
    ? `?filter=${JSON.stringify(filter)}`
    : ''
}

const checkIsBlanks = key => ['', null, undefined].includes(key);

function createHeaderDataSource(url, dataType, field, gridRef) {
  return {
    store: new CustomStore({
      key: 'value',
      load: async function (loadOptions) {
        const { skip, take, searchValue, searchExpr, searchOperation } = loadOptions;

        const params = {
          group: [{ selector: field, isExpanded: false }],
        }

        const gridFilter = gridRef?.current?.instance?.getCombinedFilter();
        if (gridFilter?.length && !loadOptions.filter) {
          // in headerFilter we need to show only values that consider grid filters
          params.filter = removeFilterValueForCurrentHF(gridFilter, field);
        }

        // for 'datetime', 'date' and 'number' headerFilters we use desc order
        if (['datetime', 'date', 'number'].includes(dataType)) {
          params.group[0].desc = true;
        }

        const { data: grouped = [] } = await postWithToken(url, params)
          .then(response => response.json())
          .then((data) => ({ data: data.data }))
          .catch((error) => {
            console.error(`Failed to fetch HeaderFilter data: ${error.message}`);
          });

        const blanksIndex = grouped.findIndex(item => checkIsBlanks(item.key));
        if (blanksIndex > 0) {
          const blanksElement = grouped.splice(blanksIndex, 1)?.[0];
          grouped.splice(0, 0, blanksElement);
        }

        const mapped = grouped.map((item) => {
          const isBlanks = checkIsBlanks(item.key);

          let text;
          if (isBlanks) {
            text = '(Blanks)';
          } else {
            switch (dataType) {
              case 'datetime':
                text = new Date(item.key).toLocaleString();
                break;
              case 'date':
                text = format(new Date(item.key), 'MM/dd/yyyy');
                break;
              case 'boolean':
                text = item.key ? '✔' : '✖';
                break;
              default:
                text = item.key;
            }
          }

          return {
            text,
            value: isBlanks ? null : item.key,
          };
        });

        let query = Query(mapped);

        if (searchValue) {
          query = query.filter([searchExpr, searchOperation, searchValue]);
        }

        query = query.slice(skip, take);

        return query.toArray();
      }
    })
  }
}

function removeFilterValueForCurrentHF(gridFilter, field) {
  return removeCondtionsFromFilter(removeValueFromFilter(gridFilter, field));
}

function removeCondtionsFromFilter(array) {
  const lastElementIndex = array.length - 1;
  const conditions = ['or', 'and'];

  return array.filter((item, index) => {
    const isFirstOrLastElement = conditions.includes(item)
      && (index === 0 || index === lastElementIndex);
    const isFewConditionsTogether = conditions.includes(item)
      && conditions.includes(array[index + 1]);

    return !(isFirstOrLastElement || isFewConditionsTogether);
  });
}

function removeValueFromFilter(gridFilter, field) {
  // headerFilter for which has applied some options need to includes all options
  return gridFilter.filter((item) => {
    let result = true;

    if (!Array.isArray(item) && gridFilter[0] === field) result = false;

    if (Array.isArray(item)) {
      if (!Array.isArray(item[0]) && item[0] === field) result = false;
    }

    if(Array.isArray(item?.[0])) {
      if (!Array.isArray(item[0][0]) && item[0][0] === field) result = false;
    }

    // check if it's the negation for item
    if (!Array.isArray(item) && gridFilter[0] === "!" && (gridFilter[1][0] === field || gridFilter[1][0][0] === field)) {
      result = false;
    }
    if (Array.isArray(item) && !Array.isArray(item[0]) && item[0] === "!" && (item[1][0] === field || item[1][0][0] === field)) {
      result = false;
    }
    if(Array.isArray(item?.[0]) && !Array.isArray(item[0][0]) && item[0][0] === "!" && (item[0][1][0] === field || item[0][1][0][0] === field)) {
      result = false;
    }

    return result;
  });
}

function defaultSortingClick(e) {
  if (e.rowType === 'header' && e.column && e.column.allowSorting && !e.column.sortOrder) {
    setTimeout(() => {
      e.component.columnOption(e.column.dataField, 'sortOrder', 'desc');
    });
  }
}

const exportToExcel = (gridRef, fileName = 'DataGrid', sheetName = 'Sheet', customizeCell) => {
  const workbook = new ExcelJS.Workbook(); 
  const worksheet = workbook.addWorksheet(sheetName);

  exportDataGrid({
      component: gridRef.current.instance,
      worksheet: worksheet,
      customizeCell: customizeCell && (options => customizeCell(options))
  }).then(function() {
      workbook.xlsx.writeBuffer().then(function(buffer) {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
      });
  });
}

const dataGridExtension = {
  registerModule,
  filterOperations,
  sortFields,
  headerFilterSort,
  defaultSortingClick,
  prepareSortingForRequest,
  prepareDates,
  sortBlanksValue,
  filterBuilderCustomOperations,
  customStoreRequestParams,
  isNotEmpty,
  getLookupFilter,
  checkIsBlanks,
  createHeaderDataSource,
  removeFilterValueForCurrentHF,
  exportToExcel
}

export default dataGridExtension;
